import { fetchGraphQL, fetchClientGraphQL } from '~/hooks/graphql.js';

const productNodePrice = `
  price(format: FORMATTED)
  salePrice(format: FORMATTED)
  regularPrice(format: FORMATTED)
  type
`;

const getMinimalProductNode = `
  product {
    node {
      productId
      id
      name
      productVendor {
        name
        slug
      }
      featuredImage {
        node {
          sourceUrl(size: THUMBNAIL)
        }
      }
      uri
      defaultCurrency {
        code
        price
        regularPrice
        salePrice
        priceExTax
        regularPriceExTax
        salePriceExTax
      }
      productSaleBadges {
        percentageSaleBadge
        showEndsSoonBadge
        showEndsTodayBadge
      }
      ... on SimpleProduct { ${productNodePrice} }
      ... on VariableProduct { ${productNodePrice} }
      ... on SubscriptionProduct {
        ${productNodePrice}
        addToCartText
        signUpFee
        subscriptionRenewalDate
      }
      ... on GroupProduct { ${productNodePrice} }
      ... on ExternalProduct { ${productNodePrice} }
    }
  }
`;

export const variationProductKeys = `
  name
  price(format: FORMATTED)
  salePrice(format: FORMATTED)
  regularPrice(format: FORMATTED)
  defaultCurrency {
    code
    price
    regularPrice
    salePrice
    priceExTax
    regularPriceExTax
    salePriceExTax
  }
  productSaleBadges {
      percentageSaleBadge
      showEndsSoonBadge
      showEndsTodayBadge
    }
  databaseId
  variationName
  variationProductSpecs {
    editionNotes
    extractedSize
    includedIds
    largestZip
    numberOfFiles
    numberOfSounds
  }
  image {
    altText
    mediaItemUrl
  }
`;

// Get current variation details - note singular 'variation' vs 'variations'
const getVariationProductNode = `
  variation {
    node {
      ${ variationProductKeys }
    }
  }
`;

const sharedProductKeys = `
    name
    databaseId
    price(format: FORMATTED)
    regularPrice(format: FORMATTED)
    salePrice(format: FORMATTED)
    defaultCurrency {
      code
      price
      regularPrice
      salePrice
      priceExTax
      regularPriceExTax
      salePriceExTax
    }
    productSaleBadges {
      percentageSaleBadge
      showEndsSoonBadge
      showEndsTodayBadge
    }
    formattedContent
    formattedExcerpt
    image {
      altText
      mediaItemUrl
    }
    galleryImages {
      nodes {
        altText
        mediaItemUrl
      }
    }
    productSpecs {
      node {
        filesIncluded
        format
        metatagged
        notMetatagged
        productSoundcloudCode
        soundcloudShortcode
        soundsIncluded
        tagline
        totalDuration
        totalSize
        type
      }
    }
    productVendor {
      slug
      name
    }
    productCategories {
      nodes {
        link
        name
        uri
      }
    }
    enqueuedStylesheets {
      nodes {
        id
        handle
        src
      }
    }
    seo {
      canonical
      title
      breadcrumbs {
        text
        url
      }
      metaDesc
      metaRobotsNofollow
      metaRobotsNoindex
      opengraphAuthor
      opengraphDescription
      opengraphImage {
        altText
        mediaItemUrl
      }
      opengraphModifiedTime
      opengraphPublishedTime
      opengraphPublisher
      opengraphSiteName
      opengraphTitle
      opengraphType
      opengraphUrl
    }
    jsonLd
`

const getSimpleProductNode = `
  ... on SimpleProduct {
    ${ sharedProductKeys }
  }
  ... on SubscriptionProduct {
    ${ sharedProductKeys }
  }
  ... on VariableProduct {
    ${ sharedProductKeys }
    variations {
      nodes {
        ${ variationProductKeys }
      }
    }
  }
  ... on GroupProduct {
    ${ sharedProductKeys }
  }
  ... on ExternalProduct {
    ${ sharedProductKeys }
  }
`;

const getProductDataQuery = `
  query GetProductDataQuery($uri: String!) {
    nodeByUri(uri: $uri) {
      ${ getSimpleProductNode }
    }
  }
`;

async function getProductById(productId) {
  return await fetchClientGraphQL(`
    query Product($productId: ID!) {
      product(id: $productId, idType: DATABASE_ID) {
        ${ getSimpleProductNode }
        isPreview
        status
      }
    }
  `, { productId });
}

async function getProductPreviewById(productId) {
  return await fetchClientGraphQL(`
    query ProductPreview ($productId: Int!, $isPreview: Boolean) {
      productPreview(id: $productId, preview: $isPreview) {
        ${ getSimpleProductNode }
        isPreview
        status
      }
    }
  `, { productId });
}

const fetchProductData = (uri) => {
  return fetchGraphQL(getProductDataQuery, { uri });
};

export {
  getMinimalProductNode,
  getVariationProductNode,
  getSimpleProductNode,
  getProductDataQuery,
  fetchProductData,
  getProductById,
  getProductPreviewById,
}
